import { Column, ColumnConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import { GET_TOTAL_POSTS_COUNT } from '@frontend/data-access';
import { Card, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { RefAttributes, useEffect, useMemo, useState } from 'react';

const { RangePicker } = DatePicker;

interface TotalPostsProps {
  event?: boolean;
}

const TotalPosts = ({ event = false }: TotalPostsProps) => {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const endDate = dayjs();
  const startDate = endDate.subtract(3, 'month');
  const [date, setDate] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);

  const [getTotalPostsCount, { data: totalPosts, loading }] = useLazyQuery<{
    dashboardPostCount: {
      name: string;
      total: number;
    }[];
  }>(GET_TOTAL_POSTS_COUNT);
  const monthNames = useMemo(
    () => [
      tcommon('Jan'),
      tcommon('Feb'),
      tcommon('Mar'),
      tcommon('Apr'),
      tcommon('May'),
      tcommon('Jun'),
      tcommon('Jul'),
      tcommon('Aug'),
      tcommon('Sep'),
      tcommon('Oct'),
      tcommon('Nov'),
      tcommon('Dec'),
    ],
    [tcommon]
  );

  useEffect(() => {
    if (date.length) {
      getTotalPostsCount({
        variables: {
          startDate: date[0],
          endDate: date[1],
          event: event,
        },
      });
    }
  }, [date, event, getTotalPostsCount]);

  const config: ColumnConfig & RefAttributes<unknown> = {
    data: totalPosts?.dashboardPostCount ?? [],
    height: 180,
    loading,
    xField: 'name',
    yField: 'total',
    color: '#1890FF',
    appendPadding: [20, 0, 0, 0],
    label: {
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
      textBaseline: 'bottom',
    },
    xAxis: {
      label: {
        style: { fontFamily: 'Lufga' },
        autoHide: true,
        autoRotate: false,
        labelFormatter: (v) => {
          const monthIndex = parseInt(v.split('-')[1], 10) - 1;
          return monthNames[monthIndex];
        },
      },
    },
    yAxis: {
      label: {
        style: { fontFamily: 'Lufga' },
      },
    },
  };

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDate([dateStrings[0], dateStrings[1]]);
    }
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="flex items-center flex-wrap gap-1">
            <span>
              {event ? t('total_events_over_time') : t('total_posts_over_time')}
            </span>
          </h2>
          <div>
            <RangePicker
              onChange={handleChange}
              defaultValue={[dayjs(date[0]), dayjs(date[1])]}
              style={{
                borderRadius: 20,
              }}
            />
          </div>
        </div>
        <Column {...config} />
      </div>
    </Card>
  );
};

export default TotalPosts;
